import { format } from 'date-fns';
import { ro } from 'date-fns/locale';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  BellIcon,
  BookOpenIcon,
  CalendarIcon,
  ExternalLinkIcon,
  InfoIcon,
  LinkIcon,
} from 'lucide-react';
import { type ReactNode, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { NewsFrequencyChart } from '@/components/charts/NewsFrequencyChart';
import {
  PriceChart,
  type PriceChartPoint,
} from '@/components/charts/PriceChart';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { track } from '@/lib/mixpanel';
import { cn } from '@/lib/utils';
import { NewsArticle, SymbolData } from '@/schema';
import { TimeWindow } from '@/types';
import { getPredictionDataForSymbol } from '@/utils/newsPredictions';
import { getDisplaySource } from '@/utils/newsSourceMapping';
import {
  getTimeWindowDescription,
  ROMANIAN_TIME_WINDOW_LABELS,
} from '@/utils/timeWindow';

// Helper to get sentiment type from symbol data
const getSymbolSentiment = (
  symbolData: SymbolData
): 'positive' | 'negative' | 'neutral' => {
  const predictionJson = symbolData.prediction_json;
  if (!predictionJson) return 'neutral';

  // Cast to string to handle both old Romanian format and new English format
  const sentiment = predictionJson.sentiment as string;
  if (sentiment === 'favorabil' || sentiment === 'positive') return 'positive';
  if (sentiment === 'nefavorabil' || sentiment === 'negative')
    return 'negative';
  return 'neutral';
};

const InfoAlert = ({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) => (
  <div
    className={`flex items-start rounded-md border-2 border-amber-200 bg-amber-50 p-4 ${className}`}>
    <div className="mr-2 pt-0.5 text-amber-600">
      <InfoIcon className="h-5 w-5" />
    </div>
    <div className="text-sm font-medium text-amber-900">{children}</div>
  </div>
);

const ArticleHeader = ({
  article,
  timePosted,
}: {
  article: NewsArticle;
  timePosted: string;
}) => (
  <div className="flex flex-col">
    {/* Icon + Source/Date row */}
    <div className="flex items-center gap-4">
      {/* Icon on the left */}
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-black">
        <BookOpenIcon className="h-6 w-6 text-white" strokeWidth={1.5} />
      </div>

      {/* Source + Date on the right */}
      <div className="flex flex-col">
        {/* Source Pill */}
        <div className="mb-1 inline-flex w-fit items-center rounded-md border border-neutral-400 px-2 py-0.5">
          <span className="text-sm font-medium text-neutral-700">
            {getDisplaySource(article.source)}
          </span>
        </div>

        {/* Date */}
        <div className="flex items-center text-sm text-neutral-500">
          <CalendarIcon className="mr-1.5 h-4 w-4" />
          {timePosted}
        </div>
      </div>
    </div>

    {/* Title */}
    <h2 className="mt-6 font-heading text-xl font-bold text-neutral-900 md:text-2xl lg:text-3xl">
      {article.title}
    </h2>
  </div>
);

const ArticleDescription = ({ content }: { content: string }) => {
  const paragraphs = content
    .split(/\n+/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);

  return (
    <div className="max-w-3xl">
      <div className="prose prose-neutral text-base leading-relaxed text-neutral-700">
        {paragraphs.length > 0 ? (
          paragraphs.map((paragraph, index) => (
            <p key={index} className="font-sans">
              {paragraph}
            </p>
          ))
        ) : (
          <p className="italic text-neutral-500">
            Conținutul articolului nu este disponibil.
          </p>
        )}
      </div>
    </div>
  );
};

const PredictionIndicator = ({ prediction }: { prediction: string }) => {
  const normalizedPrediction = prediction.toLowerCase();
  const isBullish = normalizedPrediction === 'favorabil';
  const isBearish = normalizedPrediction === 'nefavorabil';

  // Map old values to new display values
  const displayPrediction = isBullish
    ? 'POZITIV'
    : isBearish
      ? 'NEGATIV'
      : normalizedPrediction === 'neutru'
        ? 'NEUTRU'
        : prediction.toUpperCase();

  return (
    <div
      className={`text-xl font-bold ${
        isBullish
          ? 'text-green-600'
          : isBearish
            ? 'text-red-600'
            : 'text-neutral-700'
      }`}>
      {isBullish ? (
        <ArrowUpIcon className="mr-2 inline h-6 w-6" />
      ) : isBearish ? (
        <ArrowDownIcon className="mr-2 inline h-6 w-6" />
      ) : null}
      Sentiment: {displayPrediction}
    </div>
  );
};

const TabNavigation = ({
  children,
  defaultTab = 'prediction',
  articleId,
}: {
  children: ReactNode;
  defaultTab?: string;
  articleId: number;
}) => (
  <Tabs
    defaultValue={defaultTab}
    className="w-full"
    onValueChange={(value) =>
      track('NewsModal_TabChanged', { articleId, tab: value })
    }>
    <TabsList className="rounded-xl bg-neutral-100 p-1.5">
      <TabsTrigger className="rounded-lg" value="prediction">
        Sentiment
      </TabsTrigger>
      <TabsTrigger value="price">Grafic Preț</TabsTrigger>
      <TabsTrigger value="news">Frecvență Știri</TabsTrigger>
    </TabsList>
    {children}
  </Tabs>
);

export interface ArticleDetailsContentProps {
  article: NewsArticle;
  isActive?: boolean;
  shareUrl?: string;
}

export function ArticleDetailsContent({
  article,
  isActive = true,
  shareUrl,
}: ArticleDetailsContentProps) {
  const [selectedPriceTimeWindow, setSelectedPriceTimeWindow] =
    useState<TimeWindow>('1week');
  const [selectedNewsTimeWindow, setSelectedNewsTimeWindow] =
    useState<TimeWindow>('1week');

  // State for selected symbol (default to first symbol)
  const [selectedSymbol, setSelectedSymbol] = useState<string>(() => {
    return article.symbols?.[0]?.symbol || '';
  });

  const content = article.description || '';
  const timePosted = useMemo(() => {
    if (!article) return '';
    const timestamp = article.time_posted || article.created_at;
    if (!timestamp) return '';
    return format(new Date(timestamp), 'd MMM yyyy, HH:mm', { locale: ro });
  }, [article]);

  // Get prediction data for the selected symbol
  const predictionData = getPredictionDataForSymbol(article, selectedSymbol);

  // Check if we have multiple symbols
  const hasMultipleSymbols = article.symbols && article.symbols.length > 1;

  const handlePriceTimeWindowChange = (window: TimeWindow) => {
    setSelectedPriceTimeWindow(window);
    track('NewsModal_PriceTimeWindowChanged', {
      articleId: article.id,
      timeWindow: window,
    });
  };

  const handleNewsTimeWindowChange = (window: TimeWindow) => {
    setSelectedNewsTimeWindow(window);
    track('NewsModal_NewsTimeWindowChanged', {
      articleId: article.id,
      timeWindow: window,
    });
  };

  const handleCopyLink = () => {
    const url =
      shareUrl ||
      (typeof window !== 'undefined'
        ? `${window.location.origin}/article/${article.id}`
        : `/article/${article.id}`);
    if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) {
      toast.error('Copiere eșuată');
      return;
    }

    navigator.clipboard
      .writeText(url)
      .then(() => {
        toast.success('Link copiat!');
        track('NewsModal_LinkCopied', { articleId: article.id });
      })
      .catch((err) => {
        toast.error('Copiere eșuată');
        console.error('Copy failed:', err);
      });
  };

  const renderPredictionContent = () => {
    if (!predictionData) {
      return (
        <InfoAlert>
          Nu avem suficiente date pentru a genera o predicție AI în acest
          moment.
        </InfoAlert>
      );
    }

    return (
      <>
        <PredictionIndicator prediction={predictionData.prediction} />
        <InfoAlert className="mt-3">
          <span className="font-medium">Notă:</span> Această predicție este
          generată prin analizarea sentimentului și conținutului articolelor de
          știri recente legate de {selectedSymbol}. Vă rugăm să folosiți această
          informație ca unul dintre mulți factori în deciziile dumneavoastră de
          investiții.
        </InfoAlert>
      </>
    );
  };

  return (
    <div className="flex flex-col gap-y-2">
      <ArticleHeader article={article} timePosted={timePosted} />

      <ArticleDescription content={content} />

      {selectedSymbol?.length > 0 && selectedSymbol !== '-1' && (
        <div className="mt-2">
          {/* Inline symbol pills (shown only if multiple symbols) */}
          {hasMultipleSymbols && (
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-neutral-700">
                Alege simbolul:
              </span>
              {article.symbols.map((symbolData) => {
                const sentiment = getSymbolSentiment(symbolData);
                const isSelected = selectedSymbol === symbolData.symbol;

                return (
                  <button
                    key={symbolData.symbol}
                    type="button"
                    onClick={() => {
                      setSelectedSymbol(symbolData.symbol);
                      track('NewsModal_SymbolChanged', {
                        articleId: article.id,
                        symbol: symbolData.symbol,
                      });
                    }}
                    className={cn(
                      'rounded px-3 py-1 text-sm font-medium transition-colors',
                      // Positive sentiment styling
                      sentiment === 'positive' && [
                        isSelected
                          ? 'bg-green-primary text-white'
                          : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200',
                      ],
                      // Negative sentiment styling
                      sentiment === 'negative' && [
                        isSelected
                          ? 'bg-red-primary text-white'
                          : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200',
                      ],
                      // Neutral sentiment styling
                      sentiment === 'neutral' && [
                        isSelected
                          ? 'bg-neutral-700 text-white'
                          : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200',
                      ]
                    )}>
                    {symbolData.symbol}
                  </button>
                );
              })}
            </div>
          )}
          <TabNavigation articleId={article.id}>
            <TabsContent
              value="prediction"
              className="mt-3 min-h-[250px] focus:outline-none">
              <Card className="border border-neutral-200 shadow-md">
                <CardContent className="pt-6">
                  {renderPredictionContent()}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent
              value="price"
              className="mt-3 min-h-[300px] focus:outline-none">
              <PriceChart
                symbol={selectedSymbol}
                timeWindow={selectedPriceTimeWindow}
                onTimeWindowChange={handlePriceTimeWindowChange}
                title={`Graficul prețului pentru ${selectedSymbol}`}
                description={getTimeWindowDescription(
                  selectedPriceTimeWindow,
                  'price'
                )}
                timeWindowLabels={ROMANIAN_TIME_WINDOW_LABELS}
                emptyStateMessage={`Nu există date despre preț pentru ${selectedSymbol}`}
                emptyStateHint={null}
                tooltipSeriesLabel="Preț"
                tooltipLabelFormatter={(
                  dataPoint?: PriceChartPoint,
                  label?: string
                ) => {
                  if (dataPoint?.fullDate) {
                    return `Data: ${format(dataPoint.fullDate, 'd MMM yyyy, HH:mm', { locale: ro })}`;
                  }
                  if (label && label.trim().length > 0) {
                    return `Data: ${label}`;
                  }
                  return 'Data: N/A';
                }}
                tooltipValueFormatter={(value) =>
                  Number.isFinite(value) ? value.toFixed(2) : 'N/A'
                }
                yAxisLabel="Preț"
                queryOptions={{ enabled: isActive }}
                cardClassName="border border-neutral-200 shadow-md"
              />
            </TabsContent>

            <TabsContent
              value="news"
              className="mt-3 min-h-[300px] focus:outline-none">
              <NewsFrequencyChart
                symbol={selectedSymbol}
                timeWindow={selectedNewsTimeWindow}
                onTimeWindowChange={handleNewsTimeWindowChange}
                title={`Frecvența știrilor pentru ${selectedSymbol}`}
                description={getTimeWindowDescription(
                  selectedNewsTimeWindow,
                  'news'
                )}
                timeWindowLabels={ROMANIAN_TIME_WINDOW_LABELS}
                emptyStateMessage={`Nu s-au găsit articole de știri pentru ${selectedSymbol}`}
                emptyStateHint={null}
                seriesLabel="Articole"
                tooltipLabelFormatter={(value) => `Data: ${value}`}
                tooltipValueFormatter={(value) => `${value}`}
                queryOptions={{ enabled: isActive }}
                cardClassName="border border-neutral-200 shadow-md"
              />
            </TabsContent>
          </TabNavigation>
        </div>
      )}

      {/* Bottom Action Buttons */}
      <div className="mt-6 flex items-center justify-between gap-3 border-t border-neutral-200 pt-4">
        <div className="flex gap-2">
          {/* Copy Link Button - Secondary */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopyLink}
            className="flex items-center gap-1.5 border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50">
            <LinkIcon className="h-4 w-4" />
            <span className="hidden sm:inline">Copiază Link</span>
          </Button>

          {/* Create AI Alert Button - Secondary */}
          <Button
            variant="outline"
            size="sm"
            asChild
            className="flex items-center gap-1.5 border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50">
            <a
              href="/create-alert"
              onClick={() =>
                track('NewsModal_CreateAlertClicked', { articleId: article.id })
              }>
              <BellIcon className="h-4 w-4" />
              <span className="hidden sm:inline">Crează Alerta AI</span>
            </a>
          </Button>
        </div>

        {/* Go to Source Button - Primary */}
        <Button
          asChild
          className="bg-black px-6 font-semibold text-white hover:bg-neutral-800">
          <a
            href={article.source}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() =>
              track('NewsModal_ReadFullArticleClicked', {
                articleId: article.id,
                source: article.source,
              })
            }
            className="flex items-center gap-2">
            Sursa
            <ExternalLinkIcon className="h-4 w-4" />
          </a>
        </Button>
      </div>
    </div>
  );
}
